import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { TrendChart } from "@/components/dashboards/TrendChart";
import { DrillDownTable, type Column } from "@/components/dashboards/DrillDownTable";
import { DashboardFilters, defaultFilters, type DashboardFilterState } from "@/components/dashboards/DashboardFilters";
import { useTrend, useComplianceDrillDown } from "@/lib/dashboards";

export const Route = createFileRoute("/_authenticated/dashboards/compliance")({
  component: CompliancePage,
  head: () => ({ meta: [{ title: "Compliance analytics — MissionReady" }] }),
});

type Row = { id: string; starts_at: string; role_label: string | null; site_id: string | null; compliance_status: string };

function CompliancePage() {
  const [filters, setFilters] = useState<DashboardFilterState>(defaultFilters());
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const trend = useTrend("compliance", filters);
  const drill = useComplianceDrillDown({ siteId: filters.siteId, page, pageSize: 50, search });

  const columns: Column<Row>[] = [
    { key: "starts_at", label: "Starts", render: (r) => new Date(r.starts_at).toLocaleString() },
    { key: "role_label", label: "Role" },
    { key: "compliance_status", label: "Status", render: (r) => (
      <span className={r.compliance_status === "block" ? "text-destructive font-medium" : "text-warning font-medium"}>
        {r.compliance_status}
      </span>
    ) },
  ];

  return (
    <div className="space-y-4">
      <DashboardFilters value={filters} onChange={setFilters} />
      <TrendChart title="Compliance issues over time" points={trend.data?.points} loading={trend.isLoading} color="hsl(var(--destructive))" />
      <DrillDownTable
        title="Upcoming non-compliant shifts"
        columns={columns}
        rows={(drill.data?.rows ?? []) as Row[]}
        total={drill.data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        search={search}
        onSearchChange={setSearch}
        loading={drill.isLoading}
        csvName="compliance.csv"
      />
    </div>
  );
}
